<script setup>
// Métricas do agente: cards de resumo (hoje) + quebra por tipo de evento e
// nível, a partir do endpoint agno/agents/:id/logs/stats.
import { ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import agentLogsAPI from 'dashboard/api/agents/agentLogs';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  agentId: { type: [Number, String], required: true },
});

const { t } = useI18n();

const loading = ref(false);
const stats = ref({
  total_today: 0,
  success_count: 0,
  error_count: 0,
  avg_execution_time: null,
  total_tokens: 0,
  by_event_type: {},
  by_level: {},
});

const fetchStats = async () => {
  loading.value = true;
  try {
    const response = await agentLogsAPI.getStats(props.agentId);
    stats.value = { ...stats.value, ...response.data };
  } finally {
    loading.value = false;
  }
};

const cards = computed(() => [
  {
    key: 'total',
    label: t('AI_AGENTS.METRICS.TOTAL_TODAY'),
    value: stats.value.total_today || 0,
    icon: 'i-lucide-activity',
    color: 'text-n-brand',
  },
  {
    key: 'success',
    label: t('AI_AGENTS.METRICS.SUCCESS'),
    value: stats.value.success_count || 0,
    icon: 'i-lucide-circle-check',
    color: 'text-n-teal-10',
  },
  {
    key: 'errors',
    label: t('AI_AGENTS.METRICS.ERRORS'),
    value: stats.value.error_count || 0,
    icon: 'i-lucide-circle-alert',
    color: 'text-n-ruby-10',
  },
  {
    key: 'avg',
    label: t('AI_AGENTS.METRICS.AVG_TIME'),
    value: stats.value.avg_execution_time
      ? `${stats.value.avg_execution_time}ms`
      : '—',
    icon: 'i-lucide-timer',
    color: 'text-n-amber-10',
  },
  {
    key: 'tokens',
    label: t('AI_AGENTS.METRICS.TOKENS'),
    value: stats.value.total_tokens || 0,
    icon: 'i-lucide-coins',
    color: 'text-n-slate-11',
  },
]);

const eventRows = computed(() =>
  Object.entries(stats.value.by_event_type || {}).sort((a, b) => b[1] - a[1])
);
const levelRows = computed(() =>
  Object.entries(stats.value.by_level || {}).sort((a, b) => b[1] - a[1])
);

const successRate = computed(() => {
  const total =
    (stats.value.success_count || 0) + (stats.value.error_count || 0);
  if (!total) return null;
  return Math.round(((stats.value.success_count || 0) / total) * 100);
});

onMounted(fetchStats);
</script>

<template>
  <div class="space-y-6">
    <div class="flex items-center justify-between">
      <div>
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ t('AI_AGENTS.METRICS.TITLE') }}
        </h3>
        <p class="text-xs text-n-slate-11 mt-0.5">
          {{ t('AI_AGENTS.METRICS.SUBTITLE') }}
        </p>
      </div>
      <Button
        :label="t('AI_AGENTS.METRICS.REFRESH')"
        icon="i-lucide-refresh-cw"
        variant="faded"
        size="sm"
        :is-loading="loading"
        @click="fetchStats"
      />
    </div>

    <div v-if="loading && !stats.total_today" class="flex justify-center py-12">
      <Spinner size="lg" />
    </div>

    <template v-else>
      <!-- Cards de resumo -->
      <div class="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <div
          v-for="card in cards"
          :key="card.key"
          class="bg-n-solid-2 rounded-2xl border border-n-weak p-4"
        >
          <div class="flex items-center gap-2 mb-2">
            <span :class="[card.icon, card.color]" class="w-4 h-4" />
            <span class="text-xs text-n-slate-11">{{ card.label }}</span>
          </div>
          <p class="text-2xl font-semibold text-n-slate-12">
            {{ card.value }}
          </p>
        </div>
      </div>

      <!-- Taxa de sucesso -->
      <div
        v-if="successRate !== null"
        class="bg-n-solid-2 rounded-2xl border border-n-weak p-4"
      >
        <div class="flex items-center justify-between mb-2">
          <span class="text-sm text-n-slate-12">
            {{ t('AI_AGENTS.METRICS.SUCCESS_RATE') }}
          </span>
          <span class="text-sm font-semibold text-n-slate-12">
            {{ successRate }}%
          </span>
        </div>
        <div class="h-2 w-full rounded-full bg-n-alpha-2 overflow-hidden">
          <div
            class="h-full rounded-full bg-n-teal-9"
            :style="{ width: `${successRate}%` }"
          />
        </div>
      </div>

      <!-- Quebras -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div class="bg-n-solid-2 rounded-2xl border border-n-weak p-4">
          <h4 class="text-sm font-semibold text-n-slate-12 mb-3">
            {{ t('AI_AGENTS.METRICS.BY_EVENT') }}
          </h4>
          <div v-if="eventRows.length" class="space-y-2">
            <div
              v-for="[name, count] in eventRows"
              :key="name"
              class="flex items-center justify-between text-sm"
            >
              <span class="text-n-slate-11 truncate">{{ name }}</span>
              <span class="text-n-slate-12 font-medium">{{ count }}</span>
            </div>
          </div>
          <p v-else class="text-xs text-n-slate-10">
            {{ t('AI_AGENTS.METRICS.EMPTY') }}
          </p>
        </div>

        <div class="bg-n-solid-2 rounded-2xl border border-n-weak p-4">
          <h4 class="text-sm font-semibold text-n-slate-12 mb-3">
            {{ t('AI_AGENTS.METRICS.BY_LEVEL') }}
          </h4>
          <div v-if="levelRows.length" class="space-y-2">
            <div
              v-for="[name, count] in levelRows"
              :key="name"
              class="flex items-center justify-between text-sm"
            >
              <span class="text-n-slate-11 truncate">{{ name }}</span>
              <span class="text-n-slate-12 font-medium">{{ count }}</span>
            </div>
          </div>
          <p v-else class="text-xs text-n-slate-10">
            {{ t('AI_AGENTS.METRICS.EMPTY') }}
          </p>
        </div>
      </div>
    </template>
  </div>
</template>
